import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { WEB_SOCKET_URL } from '@/configs/site-settings'
import { AnyWebSocketMessage, WebSocketStatus } from '@/types/websocket'

const MAX_RETRIES = 3
// Server-side auth failure close codes — stop reconnecting on these
const AUTH_CLOSE_CODES = [4001, 4003]

function backoffDelay(attempt: number): number {
  return Math.min(1000 * 2 ** attempt, 30_000)
}

export interface UseWebSocketReturn {
  status: WebSocketStatus
  lastMessage: AnyWebSocketMessage | null
  sendMessage: (data: unknown) => void
  disconnect: () => void
}

export function useWebSocket(): UseWebSocketReturn {
  const token = useSelector((state: RootState) => state.user.token)

  const [status, setStatus] = useState<WebSocketStatus>('disconnected')
  const [lastMessage, setLastMessage] = useState<AnyWebSocketMessage | null>(
    null
  )

  const socketRef = useRef<WebSocket | null>(null)
  const retryCountRef = useRef(0)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMountedRef = useRef(true)
  // Keep a stable ref to the current token so the close handler can read it
  const tokenRef = useRef(token)
  tokenRef.current = token
  // Incremented on every intentional close; stale event handlers compare
  // their captured connId against this ref and bail out if they differ.
  const connectionIdRef = useRef(0)

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
  }, [])

  const connect = useCallback(() => {
    const currentToken = tokenRef.current
    if (!currentToken || !WEB_SOCKET_URL) return
    if (socketRef.current?.readyState === WebSocket.OPEN) return

    setStatus('connecting')

    // Capture a unique ID for this connection attempt. Any event handler that
    // fires after connectionIdRef has been incremented (i.e. a stale handler
    // from an old connection) will see a mismatch and bail out early.
    const connId = ++connectionIdRef.current

    const url = `${WEB_SOCKET_URL}?token=${currentToken}`
    const ws = new WebSocket(url)
    socketRef.current = ws

    ws.onopen = () => {
      if (!isMountedRef.current || connectionIdRef.current !== connId) return
      retryCountRef.current = 0
      setStatus('connected')
    }

    ws.onmessage = (event: MessageEvent<string>) => {
      if (!isMountedRef.current || connectionIdRef.current !== connId) return
      try {
        const parsed = JSON.parse(event.data) as AnyWebSocketMessage
        setLastMessage(parsed)
      } catch {
        // ignore non-JSON frames
      }
    }

    ws.onerror = () => {
      if (!isMountedRef.current || connectionIdRef.current !== connId) return
      setStatus('error')
    }

    ws.onclose = (event) => {
      if (!isMountedRef.current || connectionIdRef.current !== connId) return
      socketRef.current = null
      setStatus('disconnected')

      // Stop reconnecting on auth failure codes
      if (AUTH_CLOSE_CODES.includes(event.code)) return
      if (retryCountRef.current >= MAX_RETRIES) return

      const delay = backoffDelay(retryCountRef.current)
      retryCountRef.current += 1

      retryTimerRef.current = setTimeout(() => {
        if (isMountedRef.current) connect()
      }, delay)
    }
  }, []) // no deps — reads token/connId via refs inside

  const disconnect = useCallback(() => {
    clearRetryTimer()
    retryCountRef.current = MAX_RETRIES // prevent further auto-reconnects
    connectionIdRef.current++ // invalidate any in-flight close handler
    socketRef.current?.close()
    socketRef.current = null
    setStatus('disconnected')
  }, [clearRetryTimer])

  const sendMessage = useCallback((data: unknown) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(data))
    }
  }, [])

  // Connect when token becomes available; disconnect when it's cleared
  useEffect(() => {
    isMountedRef.current = true

    if (token) {
      retryCountRef.current = 0
      clearRetryTimer()
      // Close any existing socket (e.g. token refreshed) and open a fresh one.
      // Incrementing connectionIdRef first ensures the old socket's onclose
      // handler sees a stale connId and will not trigger a spurious reconnect.
      if (socketRef.current) {
        connectionIdRef.current++
        socketRef.current.close()
        socketRef.current = null
      }
      connect()
    } else {
      clearRetryTimer()
      connectionIdRef.current++ // invalidate old connection's events
      socketRef.current?.close()
      socketRef.current = null
      setStatus('disconnected')
    }

    return () => {
      isMountedRef.current = false
      clearRetryTimer()
      // eslint-disable-next-line react-hooks/exhaustive-deps
      connectionIdRef.current++ // invalidate old connection's events on unmount
      socketRef.current?.close()
      socketRef.current = null
    }
  }, [token, connect, clearRetryTimer])

  return { status, lastMessage, sendMessage, disconnect }
}
